import { buildSearchParamsAuto } from '../utils';
import {
  STRATEGY_TAG_TYPE,
  ALL_STRATEGIES_MAP_ID,
  ALL_STRATEGIES_MEMBERS_ID,
} from './constants';
import { strategyApi } from './strategyApi';

export interface MapMarker {
  id: string;
  provider_id?: string;
  name: string;
  type: 'hcp' | 'clinic' | 'center';
  soc_type?: string | null;
  soc_subtype?: string | null;
  lat: number;
  lng: number;
  chain_ids: number[];
  metrics_v2?: Record<string, Record<string, number>>;
  [key: string]: any;
}

export interface MemberItem {
  id: string;
  provider_id?: string;
  name: string;
  type: 'hcp' | 'clinic' | 'center';
  soc_type?: string | null;
  soc_subtype?: string | null;
  specialty: string | null;
  lat: string;
  lng: string;
  city: string;
  state: string;
  address_line_1: string;
  address_line_2: string;
  zip_code: string;
  added_at: string;
  distance: number | null;
  chain_ids: number[];
  metrics_v2: Record<string, Record<string, number>>;
  tags: any[];
  [key: string]: any;
}

export interface ChainMeta {
  label: string;
  target_strategy_type: string;
  color?: string;
}

export interface MetricDefinition {
  label: string;
  chain_ids: number[];
  display_options?: Array<{ code: string; label: string; type?: string }>;
  execution_params?: any;
}

export interface MapMarkersResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: MapMarker[];
  chains: Record<string, ChainMeta>;
}

export interface MembersResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: MemberItem[];
  chains: Record<string, ChainMeta>;
  metric_definitions: Record<string, MetricDefinition>;
}

interface BoundsParams {
  min_lat: number;
  max_lat: number;
  min_lng: number;
  max_lng: number;
}

export interface AllStrategiesMapParams extends Partial<BoundsParams> {
  territory_id?: number;
  bounding_territory?: number;
  chain_id?: number[];
  q?: string;
  pageSize?: number;
  page?: number;
  city?: string[];
  state?: string[];
  specialty?: string[];
  soc_type?: string[];
  soc_subtype?: string[];
  crm_status?: string[];
  provider_list?: string[];
}

export interface AllStrategiesMembersParams extends Partial<BoundsParams> {
  territory_id?: number;
  bounding_territory?: number;
  current_location_lat?: number;
  current_location_lng?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  sort_metric?: string | null;
  chain_id?: number[];
  q?: string;
  pageSize?: number;
  page?: number;
  city?: string[];
  state?: string[];
  specialty?: string[];
  soc_type?: string[];
  soc_subtype?: string[];
  crm_status?: string[];
  provider_list?: string[];
}

export interface SiteOfCareSubtypeOption {
  id: string;
  name: string;
}

export interface SiteOfCareTypeOption {
  id: string;
  name: string;
  subtypes: SiteOfCareSubtypeOption[];
}

export const allStrategiesApi = strategyApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllStrategiesMapMarkers: builder.query<
      MapMarkersResponse,
      AllStrategiesMapParams
    >({
      query: (params) => {
        const queryParams = buildSearchParamsAuto(params || {});
        return `v1/strategies/assigned-strategies/providers/map/?${queryParams?.toString()}`;
      },
      providesTags: [{ type: STRATEGY_TAG_TYPE, id: ALL_STRATEGIES_MAP_ID }],
    }),
    getAllStrategiesMembers: builder.query<
      MembersResponse,
      AllStrategiesMembersParams
    >({
      query: (params) => {
        const queryParams = buildSearchParamsAuto(params || {});
        return `v1/strategies/assigned-strategies/providers/members/?${queryParams?.toString()}`;
      },
      providesTags: [
        { type: STRATEGY_TAG_TYPE, id: ALL_STRATEGIES_MEMBERS_ID },
      ],
    }),
    getAllStrategiesMembersList: builder.infiniteQuery<
      MembersResponse,
      Omit<AllStrategiesMembersParams, 'page'>,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
          lastPage.next ? lastPageParam + 1 : undefined,
        getPreviousPageParam: (_firstPage, _allPages, firstPageParam) =>
          firstPageParam > 1 ? firstPageParam - 1 : undefined,
      },
      query: ({ queryArg, pageParam }) => {
        const queryParams = buildSearchParamsAuto({
          ...queryArg,
          page: pageParam,
          pageSize: queryArg.pageSize || 50,
        });
        return `v1/strategies/assigned-strategies/providers/members/?${queryParams?.toString()}`;
      },
      providesTags: [
        { type: STRATEGY_TAG_TYPE, id: ALL_STRATEGIES_MEMBERS_ID },
      ],
    }),
    getAllStrategiesFilterOptions: builder.query<
      { results: Array<{ id: string; name: string }> },
      { type: 'specialty' | 'city' | 'state' | 'crm-status' | 'list-membership'; chain_id?: number[]; territory_id?: number; bounding_territory?: number; q?: string; page?: number; pageSize?: number }
    >({
      query: (params) => {
        const { type, ...rest } = params;
        const queryParams = buildSearchParamsAuto(rest);
        return `v1/strategies/assigned-strategies/providers/filter-options/${type}/?${queryParams?.toString()}`;
      },
    }),
    getAllStrategiesSiteOfCareTypes: builder.query<
      { results: SiteOfCareTypeOption[] },
      { chain_id?: number[]; territory_id?: number; bounding_territory?: number }
    >({
      query: (params) => {
        const queryParams = buildSearchParamsAuto(params || {});
        return `v1/strategies/assigned-strategies/providers/filter-options/site-of-care-types/?${queryParams?.toString()}`;
      },
    }),
  }),
});

export const {
  useGetAllStrategiesMapMarkersQuery,
  useGetAllStrategiesMembersQuery,
  useLazyGetAllStrategiesMembersQuery,
  useGetAllStrategiesMembersListInfiniteQuery,
  useGetAllStrategiesFilterOptionsQuery,
  useGetAllStrategiesSiteOfCareTypesQuery,
} = allStrategiesApi;
